/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
import Vue from 'vue';
import axios from 'axios';
import { i18n } from '@/plugins/I18n.js';
import { serialize } from 'object-to-formdata';

function checkBan(response) {
  const error = response.errors.find(e => e.code === 'ERROR_DEVICE_BANNED');
  if (!response.success && error) {
    if (Vue.router.currentRoute.name !== 'ban') {
      Vue.router.push({
        name: 'ban',
        params: {
          reason: response.data,
          heading: error.translated,
        },
      });
    }
  }
}

function checkPlusRequired(response) {
  const error = response.errors.find(e => e.code === 'ERROR_PLUS_REQUIRED');
  if (!error || !response.data) {
    return;
  }

  Vue.router.push({
    path: response.data,
  });

  throw new Error(error.code);
}

// function showMaintenanceError() {
//   Vue.prototype.$alert('Appdb is in maintenance mode, please check <a href="https://status.dbservices.to">status page</a> for information.', {
//     okText: 'Refresh',
//   }).finally(() => window.location.reload());
// }

class Api {
  domain = 'https://api.dbservices.to/v1.6/';

  async call(action, data, handleErrors = true, returnFullResponse = false) {
    let response = await axios({
      method: 'GET',
      url: `${this.domain}${action}/`,
      params: {
        lang: Vue.store.getters.lang,
        lt: Vue.store.getters.lt,
        rc: Vue.store.getters.ref,
        ...data,
        brand: process.env.BRAND_CONFIG.name,
      },
      withCredentials: true,
    })
      .catch(() => {
        // showMaintenanceError()
      });

    // if (!response) {
    //   showMaintenanceError();
    // }

    response = response.data;

    checkBan(response);
    checkPlusRequired(response);

    if (!handleErrors) {
      return response;
    }

    if (!response.success) {
      if (response.errors.length && response.errors[0].code === 'ERROR_MAINTENANCE_MODE') {
        // eslint-disable-next-line no-restricted-globals
        location.href = `/maintenance#${Vue.store.state.theme}`;
        return null;
      }

      Vue.notify({
        type: 'error',
        text: response.errors[0].translated + (typeof response.data === 'string' && response.data ? `. ${response.data}` : ''),
      });

      throw new Error(response.errors[0].code);
    }

    if (returnFullResponse) {
      return response;
    }

    return response.data;
  }

  async post(action, data, handleErrors = true, config) {
    const form = serialize(data);

    for (const key of form.keys()) {
      if (!form.get(key)) {
        form.delete(key);
      }
    }

    let link = `${this.domain}${action}/?lt=${Vue.store.getters.lt}&lang=${Vue.store.getters.lang}&brand=${process.env.BRAND_CONFIG.name}`;
    if (Vue.store.getters.refCode) {
      link += `&ref_code=${Vue.store.getters.refCode}`;
    }
    const response = await axios.post(link, form, config);

    const result = response.data;

    checkBan(result);

    if (handleErrors) {
      if (!result.success) {
        Vue.notify({
          type: 'error',
          text: result.errors[0].translated + (typeof result.data === 'string' && result.data ? `. ${result.data}` : ''),
        });

        throw new Error(result.errors[0].code);
      }

      return result.data;
    }

    return result;
  }

  async search({
    type,
    price = 0,
    genre = 0,
    dev = 0,
    order = 'added',
    page = 1,
    q = '',
  }) {
    return this.call('search', {
      type,
      price,
      genre,
      dev,
      order,
      length: 25,
      start: (page - 1) * 25,
      q,
    });
  }

  async getGenres() {
    return this.call('list_genres');
  }

  async getNews({
    limit = 3,
    id = null,
  } = {}) {
    return this.call('get_pages', {
      category: 'news',
      length: limit,
      id,
    });
  }

  async getArticle(id) {
    return this.call('get_pages', {
      category: 'articles',
      id,
    });
  }

  async getAppDetails({
    type,
    trackid,
  }) {
    return this.call('search', {
      type,
      trackid,
    });
  }

  async getLinks({
    type,
    trackid,
  }) {
    return this.call('get_links', {
      type,
      trackids: [trackid],
    }, false);
  }

  async getRequests(page, filter = {}) {
    return this.call('get_publish_requests', {
      type: 'ios',
      length: 25,
      start: (page - 1) * 25,
      ...filter,
    });
  }

  async link({
    email = '',
    code = '',
    type = 'new',
  }, handleErrors = false) {
    return this.call('link', {
      email,
      link_code: code,
      type,
    }, handleErrors);
  }

  async getDevices(handleErrors = false) {
    return this.call('get_all_devices', {}, handleErrors);
  }

  async getLinkCode() {
    return this.call('get_link_code');
  }

  async getDeviceStatus() {
    return this.call('get_status');
  }

  async retryCommand(uuid) {
    return this.call('retry_command', { uuid });
  }

  async fixCommand(uuid) {
    return this.call('fix_command', { uuid });
  }

  async clearCommands() {
    return this.call('clear');
  }

  async getUpdates() {
    const loc = window.location.href;
    const get = (ticket, resolve, reject) => {
      this.call('get_updates', { t: ticket }, false)
        .then((response) => {
          if (response.success) {
            resolve(response.data);
            return;
          }
          if (response.errors.some(e => e.code === 'ERROR_NOT_READY')) {
            const timeout = setTimeout(() => {
              get(ticket, resolve, reject);
            }, 2000);
            if (loc !== window.location.href) {
              clearTimeout(timeout);
            }
            return;
          }

          reject(response.errors);
        });
    };
    return this.call('get_update_ticket')
      .then(ticket => new Promise((resolve, reject) => get(ticket, resolve, reject)));
  }

  async getConfiguration(handleErrors = true) {
    return this.call('get_configuration', {}, handleErrors);
  }

  async saveConfiguration(config) {
    const params = {};
    Object.keys(config)
      .forEach((key) => {
        params[`params[${key}]`] = config[key];
      });

    const getCodeAndSave = () => Vue.prototype.$prompt(i18n.t('ENTER_OTP'))
      .then((code) => {
        params['params[2fa_code]'] = code;
        return this.post('configure', params);
      })
      .catch((e) => {
        if (e && e.message === 'ERROR_2FA_CODE_IS_WRONG') {
          return getCodeAndSave();
        }

        throw e;
      });

    return this.post('configure', params)
      .catch((e) => {
        if (e && e.message === 'ERROR_2FA_CODE_IS_REQUIRED') {
          return getCodeAndSave();
        }

        throw e;
      });
  }

  async installApp({
    id,
    type,
    features,
    isFromRepo,
    name,
    link,
    image,
  }) {
    return this.call('get_protection_validation_id', {}, false)
      .then(response => new Promise((resolve, reject) => {
        if (!response.success) {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject([response.errors[0].code, response.errors[0].translated]);
        }

        const options = {
          enable_features: features,
          type,
          validation_id: response.data,
        };

        if (!isFromRepo) {
          options.id = id;
        } else {
          options.bundle_id = id;
          options.name = name;
          options.link = link;
          options.image = image;
        }

        this.post('install', options, false)
          .then((resp) => {
            if (!resp.success) {
              // eslint-disable-next-line prefer-promise-reject-errors
              reject([resp.errors[0].code, resp.errors[0].translated]);
              return;
            }

            Vue.store.dispatch('getConfig');
            resolve();
          })
          .catch(reject);
      }));
  }

  async reportLink({
    id,
    type,
    reason,
  }) {
    return this.call('report', {
      id,
      type,
      reason,
    });
  }

  async activateVoucher(voucher) {
    return this.call('activate_pro', { voucher });
  }

  async uploadApp({
    file,
    id,
  }, progressCb) {
    return this.post('add_ipa', {
      ipa: file,
      job_id: id,
    }, false, { onUploadProgress: progressCb });
  }

  async uploadAppByUrl({
    url,
    id,
  }) {
    return this.post('add_ipa', {
      url,
      job_id: id,
    });
  }

  async getAppsJobs() {
    return this.call('get_ipa_analyze_jobs');
  }

  async deleteAppJob(id) {
    return this.call('delete_ipa_analyze_job', { id });
  }

  async getIpas() {
    return this.call('get_ipas');
  }

  async deleteIpa(id) {
    return this.call('delete_ipa', { id });
  }

  async getProPurchaseOptions() {
    return this.call('get_pro_purchase_options');
  }

  async getPlusPurchaseOptions() {
    return this.call('get_plus_purchase_options');
  }

  async reactivatePro() {
    return this.call('reactivate_pro');
  }

  async getLanguages() {
    return this.call('get_languages');
  }

  async requestApp(url, version) {
    return this.call('create_publish_request', {
      type: 'ios',
      url,
      version,
    });
  }

  async getRedirectParams(ticket) {
    return this.call('process_redirect', {
      t: ticket,
    });
  }

  async getRedirectLink(ticket) {
    return this.call('process_redirect', {
      rt: ticket,
    });
  }

  async getPromotions() {
    return this.call('get_promotions');
  }

  async getStats() {
    return this.call('get_stats');
  }

  async purchaseWithBTC({
    mode,
    txid,
    ticket,
  }) {
    return this.call('bitcoin_purchase', {
      mode,
      txid,
      purchase_ticket: ticket,
    });
  }

  async getIPACacheStatus(lt) {
    const options = {};
    if (lt) {
      options.lt = lt;
    }
    return this.call('get_ipa_cache_status', options, true, true);
  }

  async clearIPACache() {
    return this.call('clear_ipa_cache');
  }

  async revalidateIPACache() {
    return this.call('ensure_ipa_cache');
  }

  async installFromCache() {
    return this.call('install_from_cache');
  }

  async transferIPACache(lt) {
    return this.call('transfer_ipa_cache', {
      from_lt: lt,
    });
  }

  async removeAppFromIPACache(id) {
    return this.call('delete_ipa_from_cache', {
      bundle_id: id,
    });
  }

  async unlinkDevice(lt) {
    return this.call('unlink', {
      device_link_token: lt,
    });
  }

  async getInstallFeatures() {
    return this.call('get_features');
  }

  cancelPlusSubscription() {
    return this.call('cancel_plus_subscription');
  }

  editPlusDevAccount({
    appleid,
    password,
    otp,
    status,
    sharedWithEmails,
  }) {
    return this.call('edit_plus_dev_account', {
      appleid,
      password,
      otp,
      status,
      shared_with_emails: sharedWithEmails,
    });
  }

  deletePlusDevAccount() {
    return this.call('delete_plus_dev_account');
  }

  getPlusDevAccountArchive() {
    return this.call('get_plus_dev_account_archive');
  }

  refundPro() {
    return this.call('refund_pro');
  }

  getDevCredentialsProviders() {
    return this.call('get_dev_credentials_providers');
  }

  getEnterpriseCertificates() {
    return this.call('get_enterprise_certs');
  }

  addAltstoreRepo(url) {
    return this.post('edit_altstore_repo', {
      url,
    }, false);
  }

  editAltstoreRepo({
    url,
    id,
    isPublic,
  }) {
    return this.post('edit_altstore_repo', {
      url,
      id,
      is_public: isPublic,
    });
  }

  deleteAltstoreRepo(id) {
    return this.call('delete_altstore_repo', {
      id,
    });
  }

  getAltstoreRepos(isPublic) {
    return this.call('get_altstore_repos', {
      is_public: isPublic,
    });
  }

  getAltstoreRepo(id) {
    return this.call('get_altstore_repos', {
      id,
    }, false);
  }

  getDylibs() {
    return this.call('get_dylibs');
  }

  addDylib({ file, url } = {}) {
    return this.post('add_dylib', {
      dylib: file,
      url,
    });
  }

  deleteDylib(name) {
    return this.post('delete_dylib', {
      name,
    });
  }

  getSideloadingOptions(type) {
    return this.call('get_sideloading_options', {
      type,
    });
  }

  getP2POffers({ currency }) {
    return this.call('get_p2p_offers', { currency });
  }

  editP2POffer({ id, operation }) {
    return this.post('edit_p2p_offer', {
      id,
      operation,
    });
  }

  editPlusDevAccountP2PSettings({
    isP2P,
    p2pPrice,
    p2pName,
    p2pPaypalEmail,
    p2pCurrency,
    p2pManualInstructions,
  }) {
    return this.post('edit_plus_dev_account_p2p_settings', {
      is_p2p: isP2P,
      p2p_price: p2pPrice,
      p2p_name: p2pName,
      p2p_paypal_email: p2pPaypalEmail,
      p2p_currency: p2pCurrency,
      p2p_manual_instructions: p2pManualInstructions,
    });
  }

  getP2PPurchases() {
    return this.call('get_p2p_purchases', null, false);
  }

  banunbanDevice({
    udid,
    reason,
  }) {
    return this.call('banunban_device', { udid, reason });
  }
}

const plugin = {
  install() {
    Vue.prototype.$api = new Api();
  },
};

export default plugin;
